<script lang="ts">
  import api, {
    fromApi,
    mapInto,
  } from "$shared/lib/api-parkito2.svelte.ts";
  import type { Payment } from "$apis/parkito2";
  import { DateModel, PriceModel } from "$models";
  import { BodyText, ConfirmModal, LabelButton, Button, Center, Toast } from "$components";
  import { writable, derived } from "svelte/store";
  import { CardInline, ListNavigationItem } from "$components";

  type Props = {
    debtorId: string;
    repayButton?: boolean,
  };
  const { debtorId, repayButton }: Props = $props();

  const pageNum = writable(0);
  const pageSize = 5;

  const paymentsCall = fromApi((page: number) =>
    api.debtorListPayments({
      body: { page, pageSize },
      path: { id: debtorId },
    })
  );

  const noMoreHistory = writable(false);
  const paymentsList = writable<Payment[]>([]);
  const loadNext = async () => {
    const newPayments = await $paymentsCall.load($pageNum + 1);
    if (newPayments.error) {
      Toast.error("Något gick fel");
      return;
    }
    $pageNum = newPayments.data.page;
    const items = newPayments.data.items;
    $paymentsList = [...$paymentsList, ...items];
    if (newPayments.data.items.length === 0) {
      $noMoreHistory = true;
    }
  };
  loadNext();

  const reloadData = async () => {
    const resp = await api.debtorListPayments({
      body: { page: 1, pageSize: pageSize * $pageNum },
      path: { id: debtorId },
    });
    if (resp.data) {
      $paymentsList = resp.data.items;
    } else {
      $paymentsList = [];
      Toast.error("Kunde inte ladda om betalningarna");
    }
  }

  const receiptSent = writable<Record<string, true>>({});

  const sendReceipt = async (paymentId: string) => {
    receiptSent.set({ ...$receiptSent, [paymentId]: true });
    try {
      const res = await api.debtorSendPaymentReceipt({
        path: { debtor_id: debtorId, payment_id: paymentId },
        body: { email: null },
      });
      if (res.error) {
        throw res.error
      }
      Toast.info("Kvitto skickat");
    } catch {
      Toast.error("Något gick fel");
    }
  };

  const paymentToRepay = writable<Payment | undefined>();
  const repayDialogOpen = writable(false);
  paymentToRepay.subscribe(pay => repayDialogOpen.set(!!pay));

  const handleRepay = async (payment: Payment) => {
    try {
      const resp = await api.debtorRefundPayment({path: { debtor_id: debtorId, payment_id: payment.id}});
      if (resp.error) {
        throw resp.error;
      }
      Toast.info("Återbetalt");
      paymentToRepay.set(undefined);
      reloadData();
    } catch {
      Toast.error("Något gick fel");
    }
  }

</script>
{#if $paymentsList.length > 0}
  <CardInline title="Köphistorik">
    <div>
      {#each $paymentsList as payment, i}
        <ListNavigationItem
          title="{new PriceModel(payment.amount).format()}kr {payment.refundedAt ? "Återbetalt" : ""}"
          description="{new DateModel(payment.performedAt).format("D MMM YYYY")} - {payment.invoice?.records[0]?.description?.slice(0, 21)}"
          isFirst={i === 0}
          isLast={i === $paymentsList.length - 1}
          labelRight={$receiptSent[payment.id] ? "Skickat" : "Skicka kvitto"}
          onLabelRightClick={$receiptSent[payment.id]
            ? undefined
            : () => sendReceipt(payment.id)}
          labelRightType={$receiptSent[payment.id] ? "disabled" : undefined}
        >
          {#if payment.refundedAt}
            <LabelButton text={"Återbetalt"} type="disabled"/>
          {:else if repayButton}
            <LabelButton 
              onClick={() => $paymentToRepay = payment} 
              text={"Återbetala"} type="success"/>
          {/if}
        </ListNavigationItem>
      {/each}
    </div>
    {#if $noMoreHistory}
      {#if $pageNum > 1}
        <Center>
          <BodyText size="s">Du har inga fler köp</BodyText>
        </Center>
      {/if}
    {:else}
      <Button
        text="Ladda mer"
        loading={$paymentsCall.isLoading()}
        onClick={loadNext}
      />
    {/if}
  </CardInline>
{/if}

{#if $paymentToRepay}
  <ConfirmModal isOpen={repayDialogOpen} confirmText="Återbetala" title="Återbetalning" onConfirm={() => handleRepay($paymentToRepay!)}>
  Är du säker att du vill återbetala {new PriceModel($paymentToRepay.amount).format(true)}kr?
</ConfirmModal>
{/if}
